import React, { useState, useEffect, useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Grid, MenuItem, Alert, CircularProgress, Divider, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, FormControl, InputLabel,
  Stack, Badge, Popper, Paper, List, ListItemButton, ListItemAvatar, ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ForumIcon from '@mui/icons-material/Forum';
import ReplyIcon from '@mui/icons-material/Reply';
import { projectAPI, userAPI, categoryAPI } from '../../api';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';

const STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
// Categories loaded from DB
const PROJECT_ROLES = ['Developer', 'Lead Developer', 'Business Analyst', 'Designer', 'QA Engineer', 'DevOps', 'Project Manager', 'Scrum Master'];

const ROLE_COLORS = {
  admin: '#FF6B35', support_team: '#4472C4', team_member: '#2E7D32',
  qa_team: '#7B1FA2', higher_authority: '#C62828',
};

// Formats a UTC timestamp string as date + time in India Standard Time.
function formatIST(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const datePart = d
    .toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' })
    .replace(/\//g, '-');
  const timePart = d.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return `${datePart}, ${timePart} IST`;
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// Turns the flat messages array (each with a reply_to_id) into a nested
// reply tree: top-level messages at the root, each with a `children` array
// of the messages that replied directly to it, and so on recursively.
function buildMessageTree(messages) {
  const byId = {};
  messages.forEach((m) => { byId[m.id] = { ...m, children: [] }; });
  const roots = [];
  messages.forEach((m) => {
    const node = byId[m.id];
    if (m.reply_to_id && byId[m.reply_to_id]) {
      byId[m.reply_to_id].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

// Renders message text with any "@Full Name" mentions highlighted — matched
// against the given list of project members so only real mentions are styled.
function renderMessageWithMentions(text, memberNames) {
  if (!text || memberNames.length === 0) return text;
  const sorted = [...memberNames].sort((a, b) => b.length - a.length); // longest names first
  const pattern = new RegExp(
    '@(' + sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b',
    'g'
  );
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <Box
        key={key++}
        component="span"
        sx={{ color: '#1565C0', fontWeight: 700, bgcolor: '#1565C012', borderRadius: 1, px: 0.4 }}
      >
        {match[0]}
      </Box>
    );
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function ProjectFormPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const { user } = useAuth();
  const { showToast } = useToast();
  const isEditOnly = ['qa_team', 'team_member'].includes(user?.role);
  const canEditStatus = user?.role === 'admin';
  const canManageCategories = ['admin', 'support_team'].includes(user?.role);
  const canUploadFiles = ['admin', 'support_team'].includes(user?.role);
  const [dbCategories, setDbCategories] = useState([]);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  // Tabs: 0 Project Details, 1 Team Members, 2 Files, 3 Communication.
  // A notification for a new project message links here with ?tab=communication
  // so clicking it lands directly on that tab instead of Project Details.
  const [tab, setTab] = useState(() => (searchParams.get('tab') === 'communication' ? 3 : 0));

  const [form, setForm] = useState({
    name: '', description: '', start_date: '', end_date: null,
    status: 'planning', priority: 'medium', category: [],
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('Developer');
  const [memberLoading, setMemberLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const messageInputRef = useRef(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const tabRef = useRef(tab);
  const canModerateMessages = ['admin', 'support_team'].includes(user?.role);

  useEffect(() => {
    userAPI.getAll().then(res => setAllUsers(res.data)).catch(() => {});
    categoryAPI.getAll().then(res => setDbCategories(res.data.map(c => c.name))).catch(() => {});
    if (isEdit) {
      projectAPI.getOne(id)
        .then(res => {
          const p = res.data;
          setForm({
            name: p.name, description: p.description || '',
            start_date: p.start_date || '', end_date: p.end_date || null,
            status: p.status, priority: p.priority, category: p.category ? p.category.split(',').map(s => s.trim()).filter(Boolean) : [],
          });
        })
        .catch(() => showToast('Failed to load project', 'error'))
        .finally(() => setFetching(false));
      loadMembers();
      loadFiles();
      loadMessages();
    }
  }, [id, isEdit]);

  // Keep a ref of the active tab so the polling interval below always
  // checks the *current* tab, not the one that was active when it started.
  useEffect(() => {
    tabRef.current = tab;
    if (tab === 3) {
      setUnreadCount(0);
      loadMessages();
    }
    // eslint-disable-next-line
  }, [tab]);

  // Poll for new project messages every few seconds so everyone viewing this
  // project — across separate sessions — sees new messages without a manual
  // refresh, and gets a red unread badge on the Communication tab if it's
  // not the tab they're currently looking at.
  useEffect(() => {
    if (!isEdit) return;
    const interval = setInterval(() => {
      projectAPI.getMessages(id)
        .then((res) => {
          const latest = res.data;
          setMessages((prev) => {
            if (latest.length > prev.length && tabRef.current !== 3) {
              setUnreadCount((u) => u + (latest.length - prev.length));
            }
            return latest;
          });
        })
        .catch(() => {});
    }, 6000);
    return () => clearInterval(interval);
  }, [id, isEdit]);

  const loadFiles = () => {
    if (!id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/projects/${id}/files`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFiles(r.data)).catch(() => {});
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileUploading(true);
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/projects/${id}/files`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      loadFiles();
    } catch { showToast('Failed to upload file', 'error'); }
    finally { setFileUploading(false); e.target.value = ''; }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/projects/${id}/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      loadFiles();
    } catch { showToast('Failed to delete file', 'error'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes/1024).toFixed(1) + ' KB';
    return (bytes/1048576).toFixed(1) + ' MB';
  };

  const loadMembers = () => {
    setMemberLoading(true);
    projectAPI.getMembers(id)
      .then(res => setMembers(res.data))
      .catch(() => {})
      .finally(() => setMemberLoading(false));
  };

  const loadMessages = () => {
    setMessageLoading(true);
    projectAPI.getMessages(id)
      .then(res => setMessages(res.data))
      .catch(() => {})
      .finally(() => setMessageLoading(false));
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(/@([a-zA-Z ]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionAnchor(e.target);
    } else {
      setMentionAnchor(null);
      setMentionQuery('');
    }
  };

  const selectMention = (name) => {
    const input = messageInputRef.current;
    const cursorPos = input ? input.selectionStart : newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const newTextBefore = textBeforeCursor.replace(/@([a-zA-Z ]*)$/, '@' + name + ' ');
    const updated = newTextBefore + textAfterCursor;
    setNewMessage(updated);
    setMentionAnchor(null);
    setMentionQuery('');
    setTimeout(() => {
      if (input) {
        const pos = newTextBefore.length;
        input.focus();
        input.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const mentionCandidates = members
    .filter((m) => m.member?.name?.toLowerCase().includes(mentionQuery.toLowerCase()))
    .slice(0, 6);

  // Extracts the project member ids actually @mentioned in a message, by
  // matching "@Full Name" substrings against the current member list.
  const extractMentionedIds = (text) => {
    return members
      .filter((m) => m.member?.name && new RegExp('@' + m.member.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b').test(text))
      .map((m) => m.member.id);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const mentions = extractMentionedIds(newMessage.trim());
      await projectAPI.addMessage(id, { message: newMessage.trim(), mentions, reply_to_id: replyingTo?.id || null });
      setNewMessage('');
      setReplyingTo(null);
      loadMessages();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const startReply = (m) => {
    setReplyingTo(m);
    setEditingMessageId(null);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const cancelReply = () => setReplyingTo(null);

  const startEditMessage = (m) => {
    setEditingMessageId(m.id);
    setEditingText(m.message);
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleSaveEditMessage = async (messageId) => {
    if (!editingText.trim()) return;
    try {
      await projectAPI.updateMessage(id, messageId, { message: editingText.trim() });
      cancelEditMessage();
      loadMessages();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update message', 'error');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await projectAPI.deleteMessage(id, messageId);
      loadMessages();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete message', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      if (isEdit) {
        await projectAPI.update(id, { ...form, category: Array.isArray(form.category) ? form.category.join(', ') : form.category });
        showToast('Project updated successfully!', 'success');
        // Only admin/support jump to Team Members after saving — that's the
        // "finish editing, now assign the team" flow. A qa_team/team_member
        // user can only change Status/End Date here, so jumping them to a
        // tab they can't act on made it look like their change didn't save;
        // keep them on Project Details so they see the status they just set.
        if (!isEditOnly) setTab(1);
      } else {
        const res = await projectAPI.create({ ...form, category: Array.isArray(form.category) ? form.category.join(', ') : form.category });
        if (location.state?.returnTo) {
          // Came from a shortcut (e.g. "Add Project" inside the New Ticket
          // modal) — go straight back there with the new project pre-selected,
          // instead of the usual team-assignment redirect.
          showToast('Project created!', 'success');
          navigate(location.state.returnTo, { state: { reopenNewTicket: true, newProjectId: res.data.id } });
        } else {
          showToast('Project created! Redirecting to team assignment...', 'success');
          navigate('/projects/' + res.data.id + '/edit');
        }
        return;
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save project', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;
    try {
      await projectAPI.addMember(id, { user_id: selectedUser, role_in_project: selectedRole });
      setAddDialog(false);
      setSelectedUser('');
      setSelectedRole('Developer');
      loadMembers();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await projectAPI.removeMember(id, userId);
      loadMembers();
    } catch {
      showToast('Failed to remove member', 'error');
    }
  };

  const assignedUserIds = members.map(m => m.user_id);
  const availableUsers = allUsers.filter(u => !assignedUserIds.includes(u.id));

  if (fetching) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  // Renders one message plus all of its replies nested underneath it,
  // indented and connected by a left border so the conversation reads as
  // a reply tree rather than a flat list.
  const renderMessageNode = (m, depth) => {
    const isOwn = m.user_id === user?.id;
    const canDelete = isOwn || canModerateMessages;
    const isEditing = editingMessageId === m.id;
    return (
      <Box
        key={m.id}
        sx={{
          pl: depth > 0 ? 2 : 0,
          borderLeft: depth > 0 ? '2px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Avatar sx={{
            width: 34, height: 34, flexShrink: 0,
            bgcolor: (ROLE_COLORS[m.author?.role] || '#888') + '30',
            color: ROLE_COLORS[m.author?.role] || '#888',
            fontSize: '0.8rem', fontWeight: 700,
          }}>
            {m.author?.name?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.author?.name}</Typography>
              <Chip
                label={m.author?.role?.replace(/_/g, ' ')}
                size="small"
                sx={{
                  bgcolor: (ROLE_COLORS[m.author?.role] || '#888') + '18',
                  color: ROLE_COLORS[m.author?.role] || '#888',
                  fontWeight: 700, fontSize: '0.6rem', height: 18, textTransform: 'capitalize',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatIST(m.created_at)}
                {m.edited ? ' · edited' : ''}
              </Typography>
            </Box>

            {isEditing ? (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  autoFocus
                />
                <IconButton size="small" color="primary" onClick={() => handleSaveEditMessage(m.id)}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={cancelEditMessage}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {renderMessageWithMentions(m.message, members.map((mm) => mm.member?.name).filter(Boolean))}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Reply">
                    <IconButton size="small" onClick={() => startReply(m)}>
                      <ReplyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {isOwn && (
                    <Tooltip title="Edit message">
                      <IconButton size="small" onClick={() => startEditMessage(m)}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete message">
                      <IconButton size="small" color="error" onClick={() => handleDeleteMessage(m.id)}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
        {m.children && m.children.length > 0 && (
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {m.children.map((c) => renderMessageNode(c, depth + 1))}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} color="inherit">Back</Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{isEdit ? 'Edit Project' : 'New Project'}</Typography>
          <Typography color="text.secondary" variant="body2">
            {isEdit ? 'Update project details and manage team members' : 'Fill in the details to create a project'}
          </Typography>
        </Box>
      </Box>

      <Card>
        {isEdit && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
            >
              <Tab label="Project Details" />
              <Tab label={'Team Members (' + members.length + ')'} />
              <Tab label={'Files (' + files.length + ')'} />
              <Tab label={
                <Badge color="error" badgeContent={unreadCount} max={99} sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}>
                  {'Communication (' + messages.length + ')'}
                </Badge>
              } />
            </Tabs>
          </Box>
        )}

        <CardContent sx={{ p: 4 }}>
          {isEditOnly && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          You can update the <strong>End Date</strong> of this project. Only an admin can change its <strong>Status</strong>.
        </Alert>
      )}
          {!isEditOnly && !canEditStatus && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Only an admin can change a project's <strong>Status</strong>.
        </Alert>
      )}

          <TabPanel value={tab} index={0}>
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth required label="Project Name" disabled={isEditOnly} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Description" disabled={isEditOnly} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Category (Select multiple)"
                    disabled={isEditOnly}
                    SelectProps={{
                      multiple: true,
                      value: Array.isArray(form.category) ? form.category : [],
                      onChange: (e) => setForm({ ...form, category: e.target.value }),
                      renderValue: (selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(val => (
                            <Chip key={val} label={val} size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                              onDelete={!isEditOnly ? (e) => { e.stopPropagation(); setForm({ ...form, category: form.category.filter(c => c !== val) }); } : undefined}
                              onMouseDown={(e) => e.stopPropagation()} />
                          ))}
                        </Box>
                      )
                    }}>
                    {dbCategories.map(c => (
                      <MenuItem key={c} value={c}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, borderRadius: 0.5, border: '2px solid',
                            borderColor: (Array.isArray(form.category) && form.category.includes(c)) ? 'primary.main' : 'divider',
                            bgcolor: (Array.isArray(form.category) && form.category.includes(c)) ? 'primary.main' : 'transparent',
                            flexShrink: 0 }} />
                          {c}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                Timeline & Status
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth required label="Start Date" type="date" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="End Date" type="date" value={form.end_date || ''}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value || null })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Status" disabled={!canEditStatus} value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Priority" disabled={isEditOnly} value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/projects')} disabled={loading}>Cancel</Button>
                <Button type="submit" variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={loading}>
                  {isEdit ? 'Save Changes' : 'Create Project'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Members</Typography>
                <Typography variant="body2" color="text.secondary">Manage who works on this project</Typography>
              </Box>
              {!isEditOnly && (
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddDialog(true)}>
                  Add Member
                </Button>
              )}
            </Box>

            {memberLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Member</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>System Role</TableCell>
                      <TableCell>Project Role</TableCell>
                      <TableCell>Assigned Date</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((m) => (
                      <TableRow key={m.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '30', color: ROLE_COLORS[m.member?.role] || '#888', fontSize: '0.8rem', fontWeight: 700 }}>
                              {m.member?.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.member?.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{m.member?.email}</Typography></TableCell>
                        <TableCell>
                          <Chip label={m.member?.role?.replace(/_/g, ' ')} size="small"
                            sx={{ bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '18', color: ROLE_COLORS[m.member?.role] || '#888', fontWeight: 700, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={m.role_in_project} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{m.assigned_date}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove from project">
                            <IconButton size="small" color="error" onClick={() => handleRemoveMember(m.user_id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {members.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                          No team members assigned yet. Click "Add Member" to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ── Files Tab ── */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Project Files</Typography>
                  <Typography variant="body2" color="text.secondary">Visible to all team members</Typography>
                </Box>
                {canUploadFiles && (
                  <Button variant="contained" component="label" startIcon={<AttachFileIcon />} disabled={fileUploading}>
                    {fileUploading ? 'Uploading...' : 'Upload File'}
                    <input type="file" hidden onChange={handleFileUpload} />
                  </Button>
                )}
              </Box>
              <Stack spacing={1.5}>
                {files.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                    <AttachFileIcon sx={{ fontSize: 40, opacity: 0.15, display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography color="text.secondary">No files uploaded yet</Typography>
                  </Box>
                ) : files.map(f => (
                  <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                    <AttachFileIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatSize(f.file_size)} · {f.uploader?.name} · {f.created_at?.split('T')[0]}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Download">
                        <IconButton size="small" component="a"
                          href={`${API_URL.replace('/api', '')}/uploads/${f.filename}`} target="_blank">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {['admin', 'support_team'].includes(user?.role) && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteFile(f.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </TabPanel>

          {/* ── Communication Tab ── */}
          <TabPanel value={tab} index={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Communication</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Post updates or questions related to this project — visible to everyone on the team
              </Typography>

              {messageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              ) : (
                <Stack spacing={2} sx={{ mb: 3, maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                  {messages.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                      <ForumIcon sx={{ fontSize: 40, opacity: 0.15, display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography color="text.secondary">No messages yet. Start the conversation.</Typography>
                    </Box>
                  )}
                  {buildMessageTree(messages).map((m) => renderMessageNode(m, 0))}
                </Stack>
              )}

              <Divider sx={{ mb: 2 }} />
              {replyingTo && (
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                    mb: 1, p: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'primary.main',
                    bgcolor: 'action.hover', borderRadius: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block' }}>
                      Replying to {replyingTo.author?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 420 }}>
                      {replyingTo.message}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={cancelReply}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Stack direction="row" spacing={1.5} alignItems="flex-end">
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={4}
                  placeholder="Write a message to the project team... (@ to mention)"
                  value={newMessage}
                  onChange={handleMessageChange}
                  inputRef={messageInputRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !mentionAnchor) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                    if (e.key === 'Escape') { setMentionAnchor(null); if (replyingTo) cancelReply(); }
                  }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendIcon fontSize="small" />}
                  disabled={!newMessage.trim() || sendingMessage}
                  onClick={handleSendMessage}
                  sx={{ flexShrink: 0 }}
                >
                  Send
                </Button>
              </Stack>

              <Popper
                open={Boolean(mentionAnchor) && mentionCandidates.length > 0}
                anchorEl={mentionAnchor}
                placement="top-start"
                sx={{ zIndex: 1400 }}
              >
                <Paper sx={{ width: 260, maxHeight: 220, overflowY: 'auto', mt: -1, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: 2 }}>
                  <List dense disablePadding>
                    {mentionCandidates.map((m) => (
                      <ListItemButton key={m.member?.id} onMouseDown={(e) => { e.preventDefault(); selectMention(m.member?.name); }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '30', color: ROLE_COLORS[m.member?.role] || '#888', fontSize: '0.7rem', fontWeight: 700 }}>
                            {m.member?.name?.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={m.member?.name} secondary={m.member?.role?.replace(/_/g, ' ')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.7rem', textTransform: 'capitalize' }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select value={selectedUser} label="Select User" onChange={(e) => setSelectedUser(e.target.value)}>
                {availableUsers.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: (ROLE_COLORS[u.role] || '#888') + '30', color: ROLE_COLORS[u.role] || '#888', fontSize: '0.75rem', fontWeight: 700 }}>
                        {u.name?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email} · {u.role?.replace(/_/g, ' ')}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
                {availableUsers.length === 0 && <MenuItem disabled>All users are already assigned</MenuItem>}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Project Role</InputLabel>
              <Select value={selectedRole} label="Project Role" onChange={(e) => setSelectedRole(e.target.value)}>
                {PROJECT_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleAddMember} disabled={!selectedUser}>
            Add to Project
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}